<script setup lang="ts">
import selectChevronIcon from '@/assets/icons/blog/select-chevron.svg'
import { selectChevron } from '@/utils/backgroundIcons'
import { removeQueryParam } from '@/utils/removeQueryParam'
import type { ISearchForm } from '@/components/PlushBlog/type'
import { useIsMobile } from '@/composables/blog/isMobile'

const props = defineProps<{
    searchForm: ISearchForm
}>()

const emit = defineEmits<{
    (e: 'search', payload: string): void
}>()

const route = useRoute()

const { submit, sortOrders, heading, blogTypes, formTitle, categories, datePeriod } = props?.searchForm
const { isMobile } = useIsMobile()
const isOpen = ref(true)
const setSearchQuery = ref<string>('')
const refBlogType = ref<HTMLSelectElement>()
const refCategory = ref<HTMLSelectElement>()
const refDataPeriod = ref<HTMLSelectElement>()
const refSortBy = ref<HTMLSelectElement>()

function checkMobile() {
    isOpen.value = !isMobile.value
}

onMounted(() => {
    syncCategoriesWithUrl()
    checkMobile()
})

onBeforeMount(() => {
    setSearchQuery.value = (route.query.query as string) || ''
})

watch(
    () => isMobile.value,
    () => {
        checkMobile()
    },
)

function handleSearch() {
    const searchParams = new URLSearchParams({
        query: setSearchQuery.value || '',
        blogType: refBlogType.value?.value || '',
        categoryUrlParam: refCategory.value?.value || '',
        datePeriod: refDataPeriod.value?.value || '',
        sortOrder: refSortBy.value?.value || '',
    })
    const path = removeQueryParam(`${window.location.origin}${submit.url}`, 'query')
    const url = `${path}&${searchParams.toString()}`
    emit('search', useRemoveDuplicateQueryParams(url))
}

function syncCategoriesWithUrl() {
    const categoryParams = route.query
    if (categoryParams) {
        const { query, blogType, categoryUrlParam, datePeriod, sortOrder } = categoryParams

        if (query) {
            setSearchQuery.value = query
        }
        if (blogType) {
            refBlogType.value.value = blogType
        }
        if (categoryUrlParam) {
            refCategory.value.value = categoryUrlParam
        }
        if (sortOrder) {
            refSortBy.value.value = sortOrder
        }
        if (datePeriod) {
            refDataPeriod.value.value = datePeriod
        }
    }
}
</script>

<template>
    <h1
        class="mb-12 text-center text-2.5md font-bold md:text-5xl"
        v-html="heading"
    />
    <section class="mb-12 bg-plush-gray-40 py-10">
        <div class="mx-auto max-w-[1232px] px-3 xl:p-0">
            <h2
                class="mb-6 text-2xl font-bold md:text-2md"
                v-html="formTitle"
            />
            <form @submit.prevent="handleSearch">
                <section
                    class="mb-4 flex flex-wrap justify-between gap-2 font-medium md:flex-nowrap md:justify-normal md:gap-0"
                >
                    <div class="c-focus w-[57%] md:w-3/5">
                        <label
                            for="searchQuery"
                            class="sr-only"
                        >
                            Search Query
                        </label>
                        <input
                            id="searchQuery"
                            v-model="setSearchQuery"
                            type="text"
                            placeholder="Czego szukasz?"
                            class="h-12 w-full rounded border-2 border-plush-primary bg-white pl-3 text-plush-primary md:rounded-l md:rounded-r-none"
                        />
                    </div>
                    <div class="c-focus w-2/5 md:w-1/4">
                        <label
                            for="blogType"
                            class="sr-only"
                        >
                            Blog Type
                        </label>
                        <select
                            id="blogType"
                            ref="refBlogType"
                            class="md:border-x-none h-12 w-full rounded border-2 border-plush-primary pl-3 text-plush-primary md:rounded-none"
                            :style="{
                                backgroundImage: `url(${selectChevron})`,
                            }"
                            :disabled="blogTypes.length === 1"
                        >
                            <option
                                v-if="blogTypes.length > 1"
                                value=""
                            >
                                Wszystkie
                            </option>
                            <option
                                v-for="type in blogTypes"
                                :key="type.blogType"
                                :value="type.blogType"
                            >
                                {{ type?.name }}
                            </option>
                        </select>
                    </div>
                    <div class="w-full md:w-[15%]">
                        <button
                            type="submit"
                            class="size-full h-12 rounded bg-plush-primary px-4 text-lg font-semibold uppercase text-white hover:bg-plush-blue-900 md:rounded-l-none"
                        >
                            {{ submit?.name }}
                        </button>
                    </div>
                </section>

                <section
                    class="accordion font-medium"
                    :class="{ 'open-accordion': isOpen }"
                >
                    <div class="flex flex-wrap gap-4 md:flex-nowrap">
                        <div class="c-focus w-full md:w-4/12">
                            <label
                                for="category"
                                class="sr-only"
                            >
                                Category
                            </label>
                            <select
                                id="category"
                                ref="refCategory"
                                :tabindex="isOpen ? 0 : -1"
                                class="h-12 w-full rounded border-2 border-plush-primary bg-white pl-3 text-plush-primary"
                                :style="{
                                    backgroundImage: `url(${selectChevron})`,
                                }"
                            >
                                <option value="">Wszystkie kategorie</option>
                                <option
                                    v-for="cat in categories"
                                    :key="cat.categoryUrlParam"
                                    :value="cat.categoryUrlParam"
                                >
                                    {{ cat?.name }}
                                </option>
                            </select>
                        </div>
                        <div class="c-focus w-full md:w-4/12">
                            <label
                                for="timeRange"
                                class="sr-only"
                            >
                                Time Range
                            </label>
                            <select
                                id="timeRange"
                                ref="refDataPeriod"
                                :tabindex="isOpen ? 0 : -1"
                                class="h-12 w-full rounded border-2 border-plush-primary bg-white pl-3 text-plush-primary"
                                :style="{
                                    backgroundImage: `url(${selectChevron})`,
                                }"
                            >
                                <option value="">Dowolny czas</option>
                                <option
                                    v-for="time in datePeriod"
                                    :key="time.datePeriod"
                                    :value="time.datePeriod"
                                >
                                    {{ time.label }}
                                </option>
                            </select>
                        </div>
                        <div class="c-focus w-full md:w-4/12">
                            <label
                                for="sortBy"
                                class="sr-only"
                            >
                                Sort By
                            </label>
                            <select
                                id="sortBy"
                                ref="refSortBy"
                                :tabindex="isOpen ? 0 : -1"
                                class="h-12 w-full rounded border-2 border-plush-primary bg-white pl-3 text-plush-primary"
                                :style="{
                                    backgroundImage: `url(${selectChevron})`,
                                }"
                            >
                                <option
                                    v-for="sort in sortOrders.slice().reverse()"
                                    :key="sort.sortOrder"
                                    :value="sort.sortOrder"
                                >
                                    {{ sort.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                </section>
                <p
                    class="c-focus mt-9 flex justify-center gap-2 text-center text-sm font-light md:hidden"
                    tabindex="0"
                    @click="isOpen = !isOpen"
                    @keydown.enter.prevent="isOpen = !isOpen"
                    @keydown.space.prevent="isOpen = !isOpen"
                >
                    {{ isOpen ? 'mniej filtrów' : 'więcej filtrów' }}
                    <AtomImage
                        :src="selectChevronIcon"
                        :img-class="[isOpen ? 'rotate-180' : 'rotate-0']"
                    />
                </p>
            </form>
        </div>
    </section>
</template>

<style lang="scss" scoped>
select {
    width: 100%;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-repeat: no-repeat;
    background-position: right 20px center;
}

.accordion {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 500ms;
}
.accordion.open-accordion {
    grid-template-rows: 1fr;
}
.accordion > div {
    overflow: hidden;
}

input,
.c-focus,
button,
select {
    position: relative;
    &:focus,
    &:focus-within {
        outline: none;
        &::after {
            content: '';
            outline: none;
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            border: 2px solid #fe19ff;
            border-radius: 4px;
        }
    }
}
</style>
